import { useEffect, lazy, Suspense } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Route, Routes, Navigate, useNavigate, useLocation } from "react-router-dom";

import useSocket from "./hooks/useSocket";
import useCometChat from "./hooks/useCometChat";

import {
  FallbackComponent,
  FrontendAdminPath,
  AdminPath,
  ClientPath,
  ExpertPath,
  BothUserPath
} from "./routerPaths"

// front-end Pages
import FrontEnd from "./pages/front-end";
import RegisterPage from "./pages/signup";
import LoginPage from "./pages/LoginPage";
import Page404 from "./pages/Page404";
import ThankyouPage from "./pages/ThankyouPage";

// common pages
import EmailVerification from "./pages/common/EmailVerification";
import OTPVerification from "./pages/common/otp-verification/OTPVerification";

import { fetchAllList } from "./redux/common/commonActions";
import { fetchNotifications, bothPanelActiveAs } from "./redux/user/userActions";
import PrepostScam from "./pages/PrepostScam";
import SingleScamPost from "./components/scam_section/SingleScamPost";

//common
const DashboardLayout = lazy(() => import("./layouts/dashboard/DashboardLayout"));

export default function Router() {


  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const {
    isLoggedIn,
    userRole,
    otp_verified,
    email_verified,
    isActiveAs,
    userID,
    accessToken
  } = useSelector((state) => state.userState);
  const commonState = useSelector((state) => state.commonState);
  const cometChatState = useSelector((state) => state.cometChatState);

  const getNavDirection = (to, defaultTo, typeRole, typeActive) => {
    if (isLoggedIn && userRole === to) {
      return (<Suspense fallback={<FallbackComponent />}>
        <DashboardLayout role={typeRole} bothPanelValue={typeActive} />
      </Suspense>)
    } else {
      return (<Navigate replace to={defaultTo} />)
    }
  }

  const handlePageLoadTasks = () => {
    //only if required    
    if (commonState?.countryData?.data?.length === 0 || !commonState?.countryData?.data) {
      dispatch(fetchAllList("fetchCountries"));
    }
    dispatch(fetchAllList("highestDegree"));

    if (commonState?.tasksOutsourcedData?.data?.length === 0 || !commonState?.tasksOutsourcedData?.data) {
      dispatch(fetchAllList("tasksOutsourced"));
    }
    dispatch(fetchAllList("specialization"));
    if (commonState?.wordCountData?.data?.length === 0 || !commonState?.wordCountData?.data) {
      dispatch(fetchAllList("wordCount"));
    }

    ["contentTeamSpecialization","contentWriterSpecialization","contentWritingIndustries","contentWriterIndustries","contentWritingTeamStrength"].forEach((type) => {
      if (!commonState?.[type]?.data || commonState?.[type]?.data?.length === 0) {
        dispatch(fetchAllList(type));
      }
    });

    //always load new data
    dispatch(fetchAllList("subjects"));
    dispatch(fetchAllList("academicLevel"));
    dispatch(fetchAllList("services"));
    dispatch(fetchAllList("university"));
  };

  const handleReDirections = () => {
    const isAdminRole = userRole === "SuperAdmin" || userRole === "Admin"

    const checkUrlFlow = (urlFlowFragment) => {
      return !location?.pathname?.includes(urlFlowFragment)
    }

    if (otp_verified !== 2 && !isAdminRole) {
      navigate("/otp-verification");
    } else if (email_verified !== 2 && !isAdminRole) {
      navigate("/email-verification");
    } else {
      switch (userRole) {
        case "Client":
          if (checkUrlFlow('client')) {
            navigate("/client/orders");
          }
          break;

        case "Expert":
          if (checkUrlFlow('expert')) {
            navigate("/expert/orders");
          }
          break;

        case "Client/Expert":
          if (commonState?.openLoggedInRoute &&
            Object?.keys(commonState?.openLoggedInRoute)?.length > 0) {
            const gotoRouteData = commonState?.openLoggedInRoute
            if (gotoRouteData?.userType && gotoRouteData?.userType !== isActiveAs) {
              dispatch(bothPanelActiveAs(gotoRouteData?.userType))
            }

            if (gotoRouteData?.path && gotoRouteData?.path?.trim()?.length > 0) {
              navigate(gotoRouteData?.path);
            }
          }

          if (checkUrlFlow('client-expert')) {
            navigate("/client-expert/orders");
          }
          break;

        case "SuperAdmin":
          if (checkUrlFlow('super-admin')) {
            navigate("/super-admin/users");
          }
          break;

        case "Admin":
          if (checkUrlFlow('admin-dashboard')) {
            navigate("/admin-dashboard/users");
          }
          break;

        default:
          navigate("/");
          break;
      }
    }
  }

  useEffect(() => {
    if (isLoggedIn) {
      setTimeout(() => {
        handlePageLoadTasks();
      }, 500)
    }
  }, [isLoggedIn, userRole, otp_verified, email_verified, userID]);

  useEffect(() => {
    if (isLoggedIn && userRole) {
      handleReDirections()
    }
  }, [location, isLoggedIn, userRole])

  useEffect(() => {
    if (cometChatState?.cometChatUserState && cometChatState.cometChatUserState === 1) {
      dispatch(fetchNotifications(accessToken, { to_user_id: userID }));
    }
  }, [cometChatState])

  useSocket();
  useCometChat();

  return (
    <Routes>
      <Route exact path="/" element={<FrontEnd />} />
      <Route exact path="/login" element={<LoginPage />} />
      <Route exact path="/reported-scams" element={<PrepostScam />} />
      <Route exact path="/signup" element={<RegisterPage />} />
      <Route path="/signup/:referralLink" element={<RegisterPage />} />
      <Route exact path="/otp-verification" element={<OTPVerification />} />
      <Route exact path="/email-verification" element={<EmailVerification />} />
      <Route exact path="/thank-you" element={<ThankyouPage />} />
      <Route exact path="/not-found" element={<Page404 />} />
      <Route path="/report-a-scam/:id" element={<SingleScamPost />} />

      {/* Route to FrontendAdminPath should be before the wildcard route */}
      <Route path="/*" element={<FrontendAdminPath />} />

      {/* Wildcard route for 404 page should be at the end */}
      <Route path="*" element={<Page404 />} />

      <Route
        path="/client/*"
        element={getNavDirection("Client", "/login", userRole)}
      >
        <Route path="*" element={<ClientPath />} />
      </Route>

      <Route
        path="/expert/*"
        element={getNavDirection("Expert", "/login", userRole)}
      >
        <Route path="*" element={<ExpertPath />} />
      </Route>

      <Route
        path="/client-expert/*"
        element={getNavDirection("Client/Expert", "/login", userRole, isActiveAs)}
      >
        <Route path="report-a-scam/:id" element={<SingleScamPost />} />
        <Route path="*" element={<BothUserPath />} />
      </Route>

      <Route
        path="/super-admin/*"
        element={getNavDirection("SuperAdmin", "/admin-login", userRole)}
      >
        <Route path="*" element={<AdminPath userRole={userRole} />} />
      </Route>

      <Route
        path="/admin-dashboard/*"
        element={getNavDirection("Admin", "/admin-login", userRole)}
      >
        <Route path="*" element={<AdminPath userRole={userRole} />} />
      </Route>
    </Routes>
  );
}
